import React, { useCallback, useEffect, useState } from 'react';
import { APP_TIMEZONE } from '../../config';
import { errorMessage } from '../admin/adminApi';
import { useQueryParams } from '../admin/useQueryParams';
import { formatBytes, formatDate } from '../admin/format';
import {
  Badge, DataTable, Drawer, ListLayout, Panel, SearchInput, Spinner, Toolbar,
  btnDanger, btnPrimary, btnSecondary, inputClass, labelClass, selectClass
} from '../admin/AdminUI';
import { AUDIENCE_LABELS, TEXT_STATUS_LABELS, downloadFile, keepUntilLabel } from './documentUtils';
import DocumentContentFields from './DocumentContentFields';
import ReadableContent from './ReadableContent';

const todayInputValue = () => new Date().toLocaleDateString('en-CA', { timeZone: APP_TIMEZONE });

// `writing` is the Admin's form with the readable text; without it, a file is all an item needs.
function DocumentForm({ categories, doc, writing = false, canAttach = false, api, base, notify, onSubmit, onCancel, submitLabel }) {
  const [form, setForm] = useState(() => {
    const category = doc?.category || (writing ? 'guide' : 'minutes');
    return {
      title: doc?.title || '',
      category,
      audience: doc?.audience || categories[category]?.audience || 'council',
      documentDate: doc?.documentDate ? doc.documentDate.slice(0, 10) : todayInputValue(),
      description: doc?.description || '',
      keepPermanently: !!doc?.keepPermanently,
      ...(writing ? { content: doc?.content || '', images: doc?.images || [], priority: doc?.priority || 0 } : {})
    };
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const selected = categories[form.category];
  const permanentByCategory = selected?.years === null && !selected?.notRecord;
  const showFile = !doc || canAttach;

  const chooseFile = (chosen) => {
    setFile(chosen);
    if (chosen && !form.title) setForm((prev) => ({ ...prev, title: chosen.name.replace(/\.[^.]+$/, '').slice(0, 200) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(form, file);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {showFile && (
        <div>
          <label htmlFor="document-file" className={labelClass}>{writing ? 'Original file, optional' : 'File *'} (up to 25 MB)</label>
          <input id="document-file" type="file" required={!writing} onChange={(e) => chooseFile(e.target.files[0] || null)} className="text-sm text-foreground" />
        </div>
      )}
      <div>
        <label htmlFor="document-title" className={labelClass}>Title *</label>
        <input id="document-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} placeholder="e.g., Council meeting minutes, Oct 22" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="document-category" className={labelClass}>Category</label>
          <select
            id="document-category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value, audience: doc ? form.audience : categories[e.target.value].audience })}
            className={inputClass}
          >
            {Object.entries(categories).map(([key, c]) => <option key={key} value={key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="document-audience" className={labelClass}>Who can see it</label>
          <select id="document-audience" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className={inputClass}>
            {Object.entries(AUDIENCE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="document-date" className={labelClass}>Document date</label>
          <input id="document-date" type="date" value={form.documentDate} onChange={(e) => setForm({ ...form, documentDate: e.target.value })} className={inputClass} />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-foreground pb-2">
            <input type="checkbox" checked={permanentByCategory || form.keepPermanently} disabled={permanentByCategory} onChange={(e) => setForm({ ...form, keepPermanently: e.target.checked })} />
            Keep permanently
          </label>
        </div>
      </div>
      <div>
        <label htmlFor="document-description" className={labelClass}>Short description</label>
        <textarea id="document-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
      </div>
      {writing && <DocumentContentFields form={form} setForm={setForm} doc={doc} api={api} base={base} notify={notify} />}
      <p className="text-xs text-muted-foreground">
        {selected?.notRecord
          ? `${selected.label} aren't records and can be deleted at any time.`
          : <>{permanentByCategory ? `${selected.label} are kept permanently.` : `${selected.label} are kept ${selected.years} years from the document date.`} Records can't be deleted before then, except right after a mistaken upload.</>}
      </p>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : submitLabel}</button>
        <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </form>
  );
}

// The Handbook's items as the Admin and council see them (context/Handbook.md). `base` is the
// documents path for `api`; `canEdit` is false when council is in preview without a seat; `writing`
// is the Admin's ability to write the readable text and attach originals.
function DocumentsLibrary({ api, base, canEdit = true, practice = false, writing = false, title = 'Documents', notify = () => {} }) {
  const [params, setParams] = useQueryParams();
  const [data, setData] = useState(null);
  const q = params.get('q') || '';
  const category = params.get('category') || '';
  const selectedId = params.get('document');
  const creating = !!params.get('new');
  const writingNew = writing && params.get('new') === 'write';
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get(base, { q: q || undefined, category: category || undefined });
      setData(response.data);
    } catch (err) {
      alert(errorMessage(err, 'Failed to load documents'));
    }
  }, [api, base, q, category]);

  useEffect(() => { load(); }, [load]);

  const selected = data?.documents.find((d) => d._id === selectedId);

  // The Admin edits the text; everyone else with text to read gets it read-only.
  const needsDetail = writing || !!selected?.hasContent;

  useEffect(() => {
    setDetail(null);
    if (!needsDetail || !selectedId) return undefined;
    let current = true;
    api.get(`${base}/${selectedId}`)
      .then((response) => { if (current) setDetail(response.data.document); })
      .catch((err) => alert(errorMessage(err, 'Failed to load the item')));
    return () => { current = false; };
  }, [api, base, selectedId, needsDetail]);

  const multipart = (form, file) => {
    const body = new FormData();
    if (file) body.append('file', file);
    Object.entries(form).forEach(([key, value]) => body.append(key, key === 'images' ? JSON.stringify(value) : String(value)));
    return body;
  };

  const UPLOAD_RESULT = {
    found: 'Text found: searchable.',
    none: 'No text found: it can be downloaded but not searched.',
    not_pdf: 'Only PDFs can be searched.'
  };

  const upload = async (form, file) => {
    try {
      const response = await api.post(base, multipart(form, file));
      const { document: created } = response.data;
      notify(`Saved. ${UPLOAD_RESULT[created.textStatus] || ''}`.trim());
      setParams({ new: null, document: created._id });
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to save the item'));
    }
  };

  const save = async (form, file) => {
    try {
      const response = await api.put(`${base}/${selected._id}`, file ? multipart(form, file) : form);
      notify(file ? `Saved. ${UPLOAD_RESULT[response.data.document.textStatus] || ''}`.trim() : 'Saved.');
      if (writing) setDetail(response.data.document);
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to save the item'));
    }
  };

  const remove = async () => {
    if (!window.confirm(selected.file ? `Delete "${selected.title}"? This removes the file too.` : `Delete "${selected.title}"?`)) return;
    try {
      await api.del(`${base}/${selected._id}`);
      notify('Document deleted.');
      setParams({ document: null });
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to delete the document'));
    }
  };

  const download = (doc) => downloadFile(() => api.download(`${base}/${doc._id}/file`), doc.file.name)
    .catch((err) => alert(err?.response?.status === 404 ? 'This file is not on this server.' : 'Failed to download the document'));

  const columns = [
    {
      key: 'title',
      header: 'Document',
      render: (d) => (
        <div>
          <p className="font-medium text-foreground">{d.title}</p>
          {d.file && <p className="text-xs text-muted-foreground break-all">{d.file.name}</p>}
        </div>
      )
    },
    { key: 'category', header: 'Category', hideBelow: 'md', className: 'text-muted-foreground', render: (d) => d.categoryLabel },
    { key: 'audience', header: 'Who sees it', hideBelow: 'lg', render: (d) => <Badge className={d.audience === 'council' ? 'text-audienceOwners border-audienceOwners normal-case' : 'normal-case'}>{AUDIENCE_LABELS[d.audience]}</Badge> },
    { key: 'documentDate', header: 'Date', className: 'whitespace-nowrap text-muted-foreground', render: (d) => formatDate(d.documentDate) },
    { key: 'search', header: 'Search', hideBelow: 'xl', className: 'whitespace-nowrap text-muted-foreground', render: (d) => (d.test ? 'Practice' : TEXT_STATUS_LABELS[d.textStatus] || '—') },
    { key: 'keep', header: 'Retention', hideBelow: 'xl', className: 'whitespace-nowrap text-muted-foreground', render: (d) => { if (d.notRecord) return 'Not a record'; return d.keepUntil ? formatDate(d.keepUntil) : 'Permanent'; } }
  ];

  const drawer = creating ? (
    <Drawer title={writingNew ? 'Write an item' : 'Upload a document'} onClose={() => setParams({ new: null })}>
      {!canEdit ? <p className="text-sm text-muted-foreground">Pick who you are acting as first.</p> : !data ? <Spinner /> : (
        <DocumentForm
          key={params.get('new')}
          categories={data.categories}
          writing={writingNew}
          api={api}
          base={base}
          notify={notify}
          onSubmit={upload}
          onCancel={() => setParams({ new: null })}
          submitLabel={writingNew ? 'Save' : 'Upload'}
        />
      )}
    </Drawer>
  ) : selected ? (
    <Drawer
      title={selected.title}
      badges={<><Badge className="normal-case">{selected.categoryLabel}</Badge>{selected.test && <Badge className="text-warning border-warning">Test</Badge>}</>}
      subtitle={`${keepUntilLabel(selected)} · uploaded by ${selected.uploadedByName || 'someone'} (${selected.uploadedFrom || 'unknown'}) · ${formatDate(selected.createdAt)}`}
      onClose={() => setParams({ document: null })}
    >
      {selected.file && (
        <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
          <span className="text-foreground break-all">{selected.file.name}</span>
          <span className="text-muted-foreground">{formatBytes(selected.file.size)}</span>
          <button type="button" onClick={() => download(selected)} className={`${btnSecondary} ml-auto`}>Download</button>
        </div>
      )}
      {writing && !detail ? <Spinner /> : canEdit && (!practice || selected.test) ? (
        <>
          <DocumentForm
            key={`${selected._id}-${(writing ? detail : selected).file ? 'file' : 'none'}`}
            categories={data.categories}
            doc={writing ? detail : selected}
            writing={writing}
            canAttach={writing && !detail?.file}
            api={api}
            base={base}
            notify={notify}
            onSubmit={save}
            onCancel={() => setParams({ document: null })}
            submitLabel="Save"
          />
          <div className="mt-6 pt-4 border-t border-border">
            <button type="button" onClick={remove} className={btnDanger}>Delete</button>
          </div>
        </>
      ) : selected.description && <p className="text-sm text-foreground whitespace-pre-wrap">{selected.description}</p>}
      {!writing && selected.hasContent && (detail ? (
        <div className="mt-4 pt-4 border-t border-border">
          <ReadableContent html={detail.content} images={detail.images} title={detail.title} />
        </div>
      ) : <Spinner />)}
    </Drawer>
  ) : null;

  return (
    <ListLayout drawer={drawer} wideDrawer={writing}>
      <Panel>
        <Toolbar>
          <h2 className="text-base font-semibold text-foreground mr-2">{title}{data ? ` (${data.documents.length})` : ''}</h2>
          <SearchInput value={q} onChange={(value) => setParams({ q: value }, { replace: true })} placeholder="Search documents..." />
          <select aria-label="Category" value={category} onChange={(e) => setParams({ category: e.target.value })} className={selectClass}>
            <option value="">All categories</option>
            {data && Object.entries(data.categories).map(([key, c]) => <option key={key} value={key}>{c.label}</option>)}
          </select>
          <button type="button" onClick={() => setParams({ new: '1', document: null })} className={btnPrimary}>{writing ? '+ Upload PDF' : '+ Upload'}</button>
          {writing && <button type="button" onClick={() => setParams({ new: 'write', document: null })} className={btnSecondary}>+ Write item</button>}
        </Toolbar>
        {!data ? <Spinner /> : (
          <DataTable
            label="Documents"
            columns={writing && drawer ? columns.filter((column) => column.key === 'title') : columns}
            rows={data.documents}
            selectedId={selectedId}
            onRowClick={(d) => setParams({ document: d._id, new: null })}
            emptyMessage={q || category ? 'No documents match.' : 'No documents yet.'}
          />
        )}
      </Panel>
    </ListLayout>
  );
}

export default DocumentsLibrary;
